import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Plus, ShieldCheck, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Mascot } from "@/components/styleguide/Mascot";
import { useNarration } from "@/experiences/year1/addition/useNarration";
import { sfx, unlockAudio } from "@/experiences/year1/addition/sound";
import { CHILDREN, setActiveChild } from "@/experiences/home/worldStore";
import { STAGES } from "@/experiences/home/yearConfig";
import { ChildAvatar } from "./ChildAvatar";
import { Ambient } from "@/experiences/home/Ambient";

const BADGE = {
  early: "bg-primary/12 text-primary-strong",
  middle: "bg-teal/15 text-teal-ink",
  upper: "bg-blue/12 text-blue-ink",
};
const bandOf = (year) => (year <= 2 ? "early" : year <= 4 ? "middle" : "upper");

export default function SelectChild() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { speak } = useNarration();
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const pick = (c) => {
    if (selected) return;
    unlockAudio();
    sfx.pop();
    speak([`Hello ${c.name}!`, "Let's explore!"]);
    setActiveChild(c.id);
    setSelected(c.id);
    setTimeout(() => navigate(`/home/year${c.year}`), 1100);
  };

  return (
    <div data-testid="select-child-page" className="relative min-h-[100svh] w-full overflow-hidden bg-gradient-to-b from-[#FFF7EF] via-[#FFF1E6] to-[#DCF3EC]">
      {/* soft sky decoration */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute right-[8%] top-[6%] h-20 w-20 rounded-full bg-secondary/60 blur-[2px]" />
        <div className="absolute right-[6%] top-[4%] h-24 w-24 rounded-full bg-secondary/25 blur-2xl" />
        <motion.div className="absolute left-[8%] top-[9%] h-6 w-20 rounded-full bg-white/90" animate={reduce ? {} : { x: [0, 18, 0] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute left-[55%] top-[5%] h-5 w-14 rounded-full bg-white/80" animate={reduce ? {} : { x: [0, -14, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />
        <div className="absolute -bottom-24 left-1/2 h-[36%] w-[160%] -translate-x-1/2 rounded-[50%] bg-gradient-to-b from-teal/25 to-teal/5" />
      </div>
      <Ambient reduce={reduce} />

      {/* top bar */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 sm:px-8">
        <span className="flex items-center gap-2 rounded-full bg-card/85 py-1.5 pl-2 pr-3.5 shadow-soft backdrop-blur">
          <Mascot size={24} animated={false} />
          <span className="font-display text-base font-black tracking-tight text-foreground">Bloom</span>
        </span>
        <span data-testid="parent-chip" className="flex items-center gap-1.5 rounded-full bg-card/85 px-3.5 py-2 text-xs font-extrabold text-muted-foreground shadow-soft backdrop-blur">
          <ShieldCheck size={16} weight="fill" className="text-teal-ink" /> Parent · signed in
        </span>
      </div>

      {/* content */}
      <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-5 pb-14 pt-2 text-center sm:pt-6">
        <motion.div animate={reduce ? {} : { y: [0, -8, 0] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}>
          <Mascot size={84} animated={false} mood="encouraging" />
        </motion.div>
        <h1 className="mt-2 max-w-xl text-balance font-display text-3xl font-black tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          Who's ready for today's adventure?
        </h1>
        <p className="mt-2 text-sm font-semibold text-muted-foreground sm:text-base">Tap your explorer to enter their world.</p>

        <div className="mt-8 grid w-full grid-cols-2 gap-3.5 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
          {CHILDREN.map((c, i) => {
            const band = bandOf(c.year);
            const isPicked = selected === c.id;
            return (
              <motion.button
                key={c.id}
                data-testid={`child-card-${c.id}`}
                onClick={() => pick(c)}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0, scale: isPicked ? 1.05 : 1 }}
                transition={{ delay: reduce ? 0 : 0.08 + i * 0.06, type: "spring", stiffness: 250, damping: 20 }}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.96 }}
                className={cn(
                  "group flex min-h-[48px] flex-col items-center gap-2 rounded-3xl border-2 bg-card p-4 shadow-soft transition-colors sm:p-5",
                  isPicked ? "border-primary ring-4 ring-primary/20" : "border-transparent hover:border-primary/40 hover:shadow-soft-lg",
                )}
              >
                <ChildAvatar variant={c.avatar} className="h-16 w-16 drop-shadow-sm sm:h-20 sm:w-20" />
                <span className="font-display text-lg font-black text-foreground">{c.name}</span>
                <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-extrabold", BADGE[band])}>
                  Year {c.year} · {STAGES[c.year]}
                </span>
              </motion.button>
            );
          })}

          {/* add child */}
          <motion.button
            data-testid="add-child-button"
            onClick={() => { unlockAudio(); sfx.tap(); setShowAdd(true); }}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduce ? 0 : 0.08 + CHILDREN.length * 0.06, type: "spring", stiffness: 250, damping: 20 }}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.96 }}
            className="flex min-h-[48px] flex-col items-center justify-center gap-2.5 rounded-3xl border-2 border-dashed border-border bg-card/50 p-4 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary-strong sm:p-5"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-muted transition-colors group-hover:bg-accent sm:h-16 sm:w-16">
              <Plus size={26} weight="bold" />
            </span>
            <span className="font-display text-base font-extrabold">Add Child</span>
          </motion.button>
        </div>
      </div>

      {/* add-child note */}
      <AnimatePresence>
        {showAdd && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center px-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button aria-label="Close" onClick={() => setShowAdd(false)} className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
            <motion.div
              data-testid="add-child-sheet"
              role="dialog"
              aria-label="Add a new explorer"
              initial={{ y: 40, scale: 0.96, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="relative w-full max-w-sm rounded-[2rem] bg-card p-6 text-center shadow-soft-lg"
            >
              <button onClick={() => setShowAdd(false)} data-testid="add-child-close" aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-border">
                <X size={18} weight="bold" />
              </button>
              <Mascot size={78} animated={false} mood="encouraging" className="mx-auto" />
              <h2 className="mt-2 font-display text-xl font-black text-foreground">Add a new explorer</h2>
              <p className="mt-1.5 text-sm font-semibold text-muted-foreground">Grown-ups will be able to add another child in Family Settings — coming soon!</p>
              <button onClick={() => setShowAdd(false)} className="mt-5 inline-flex min-h-[48px] items-center rounded-full bg-primary px-7 font-display text-base font-extrabold text-primary-foreground shadow-glow transition-transform active:scale-95">
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
